import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthGetMeData } from '@hero/api';
import { useSwitchOrgApi } from '@hero/api-hooks';
import { Id } from '@hero/schema';
import { ObjectId } from 'bson';
import { useNav } from '../navigation/useNav';
import { resetSessionCache } from './resetSessionCache';
import { saveTokens } from './tokens';

/**
 * Switch the current session to a different Org (or to a solo /
 * individual context when the user is also a freelancer or individual
 * customer).
 *
 * Post-switch side effects (mobile parity):
 *   1. Persist the new access token via `saveTokens`.
 *   2. Seed `'current-user'` from the switch response so
 *      `useCurrentUser` / `isDriver` update immediately.
 *   3. Invalidate (not clear) the session cache so org-scoped
 *      queries refetch under the new identity without briefly
 *      dropping the user (which would `posthog.reset()`).
 *   4. Toast + navigate to the typed `dashboard` route.
 */
export const useSwitchOrgManager = () => {
  const { mutateAsync } = useSwitchOrgApi();
  const queryClient = useQueryClient();
  const { navigate } = useNav();
  const { t } = useTranslation(['m-user']);
  const [isSwitching, setIsSwitching] = useState(false);

  const switchOrg = useCallback(
    async (targetOrgId?: Id, allowSignInWithoutOrg = false) => {
      setIsSwitching(true);
      try {
        const result = await mutateAsync({
          targetOrgId: targetOrgId as ObjectId,
          allowSignInWithoutOrg,
        });

        saveTokens(result);
        if (result.user) {
          queryClient.setQueryData<AuthGetMeData>(['current-user'], {
            user: result.user,
          });
        }
        await resetSessionCache(queryClient, 'org-switch');
        toast.success(t('m-user:switch_org.success'));
        navigate('dashboard');

        return result;
      } catch (error) {
        toast.error(t('m-user:switch_org.error'));
        throw error;
      } finally {
        setIsSwitching(false);
      }
    },
    [mutateAsync, queryClient, navigate, t],
  );

  return { switchOrg, isSwitching };
};
